import { useCallback, useEffect } from 'react';

import type { GanttTimeLineItem } from '@bringg-frontend/gantt-timeline';
import { ExtendedTimelineItem } from '@bringg-frontend/gantt-timeline';

import useTimeWindowRange from './useTimeWindowRange';
import useStore from '../../../stores/helpers/use-context';
import { isLate } from '../timeline-logic/tasks-logic';

const useGantt = () => {
	const { routesColors, runToDriver, tasksById } = useStore();
	const {
		setGanttRef,
		ganttItems,
		ganttGroups,
		ganttColumns,
		centerMapByTask,
		onItemMoveEnded,
		onDrop,
		onDropObjectOnItem,
		ganttApi,
		getLastUpdatedRunIds,
		setLastUpdatedRunIds,
		setSelectedItems
	} = useStore().ganttStore;

	const { activeDraggedTask, translations } = useStore();
	const { showTimeWindowRange, removeTimeWindowRange, showTimeWindowRangeById } = useTimeWindowRange(
		ganttApi?.api,
		ganttItems,
		translations.timeWindowTranslations
	);

	useEffect(() => {
		if (activeDraggedTask) {
			showTimeWindowRange(activeDraggedTask.id);
		} else {
			removeTimeWindowRange();
		}
	}, [activeDraggedTask]);

	const adjustItemBeforeMoveEnded = useCallback((item: GanttTimeLineItem) => {
		item.color = routesColors.get(item.rowId);
		return item;
	}, []);

	const updateLateValue = (items: ExtendedTimelineItem[]): boolean => {
		let isChanged = false;
		items.forEach(item => {
			const id = item.id;
			if (!tasksById.has(+id)) {
				return;
			}
			const late = item.customData.late;

			const task = tasksById.get(+id);
			const wayPoint = task.way_points[0];
			const updatedLate = isLate(wayPoint);

			if (late !== updatedLate) {
				isChanged = true;
				item.customData.late = updatedLate;
			}
		});
		return isChanged;
	};

	const onItemsChange = () => {
		const timeThreshold = 7200000; // 2 hours;
		const runIds = getLastUpdatedRunIds();

		// this is a HACK fixing the issue with vistimeline not redrawing the cluster items
		if (ganttApi && runIds.length) {
			// @ts-ignore
			const { start, end } = ganttApi.api.range;

			// @ts-ignore
			ganttApi.api.itemSet.clusters.forEach(clusterItem => {
				const clusterData = clusterItem.data;
				// validation to not redraw clusters when not necessary
				const shouldRedraw =
					updateLateValue(clusterData.items) ||
					(runIds.includes(Number(clusterData.group.groupId)) &&
						clusterData.start.getTime() >= start - timeThreshold &&
						(clusterData.end?.getTime() || 0) <= end + timeThreshold);

				if (shouldRedraw) {
					clusterItem.redraw();
				}
			});

			// @ts-ignore
			updateLateValue(ganttApi.api.itemsData.get());

			setLastUpdatedRunIds([]);
		}
	};

	return {
		setGanttRef,
		ganttApi,
		ganttColumns,
		ganttGroups,
		ganttItems,
		adjustItemBeforeMoveEnded,
		centerMapByTask,
		onItemMoveEnded,
		onDrop,
		onDropObjectOnItem,
		showTimeWindowRange,
		removeTimeWindowRange,
		runToDriver,
		translations,
		onItemsChange,
		showTimeWindowRangeById,
		setSelectedItems,
		updateLateValue
	};
};

export default useGantt;
